import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Combobox, useCombobox } from 'src/design/atoms/Combobox';
import { InputBase } from 'src/design/atoms/InputBase';
import { IconSearch } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { PostDocumentDto } from 'src/api/FiretrolApiClient';
import { usePostDocumentOptions } from 'src/api/queries/posts/usePostOptions';
import useDebounce from 'src/hooks/useDebounce';

interface PostDocumentPickerPickerProps {
  jobId: number;
  onDocumentSelected: (doc: PostDocumentDto) => void;
}

export const PostDocumentPicker: React.FC<PostDocumentPickerPickerProps> = ({
  jobId,
  onDocumentSelected,
}) => {
  const [query, setQuery] = React.useState('');
  const debouncedTitle = useDebounce(query, 300);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const { data: options = [], isFetching: loading } = usePostDocumentOptions(
    jobId,
    debouncedTitle,
  );

  const items = useMemo(
    () =>
      options.map((option: PostDocumentDto) => ({
        value: option.id.toString(),
        label: option.name,
      })),
    [options],
  );

  return (
    <Paper withBorder={false} h={215} p="md">
      <Combobox
        store={combobox}
        withinPortal={false}
        onOptionSubmit={(value) => {
          const selected = options.find((opt) => opt.id.toString() === value);
          if (selected) {
            onDocumentSelected(selected);
          }
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            rightSection={
              loading ? <Loader size="xs" /> : <IconSearch size={16} />
            }
            value={query}
            onChange={(event) => {
              combobox.openDropdown();
              combobox.updateSelectedOptionIndex();
              setQuery(event.currentTarget.value);
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => {
              combobox.closeDropdown();
              setQuery('');
            }}
            placeholder="Search for documents to link..."
            rightSectionPointerEvents="none"
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {items.length > 0 ? (
              items.map((item) => (
                <Combobox.Option key={item.value} value={item.value}>
                  {item.label}
                </Combobox.Option>
              ))
            ) : (
              <Combobox.Empty>Type to find documents to link...</Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Paper>
  );
};
